import { Add, Clear, DeleteOutlined, EditOutlined, Save } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  createFilterOptions,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import NormalTable from "../../../../Component/NormalTable";
import { masterAPI } from "../../../Masters/dataConfig";
import { axiosPrivate } from "../../../../axios/axios";
import { color } from "../../../../StyleData/Config";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { partsReservationStatus } from "../../../../ConfigInfo/Info";

const CreatePart = ({ workOrderId, handleCloseClick, editData }) => {
  const [partData, setPartData] = useState([]);
  const [loadingParts, setLoadingParts] = useState(false);
  const [partSelect, setPartSelect] = useState("");
  const [srSelect, setSrSelect] = useState("");

  const [partItem, setPartItem] = useState({
    partId: null,
    stockRoomId: null,
    quantity: 0,
    stockRoom: [],
  });

  const [addedParts, setAddedParts] = useState([]);
  const [maxQuantity, setMaxQuantity] = useState("");
  const [loading, setLoading] = useState(false);
  const [plannedParts, setPlannedParts] = useState([]);

  const hasPlannedParts = plannedParts.length > 0;
  // Same part can be planned against different stock rooms, so a planned
  // part is uniquely identified by partId + stockRoomId, not partId alone.
  const partOptions = hasPlannedParts
    ? Array.from(
        new Map(
          plannedParts.map((p) => [`${p.partId}_${p.stockRoomId}`, p])
        ).values()
      )
    : partData;

  const getPartOptionLabel = (option) => {
    const codePart = (option.code || option.partCode)
      ? (option.code || option.partCode) + " - "
      : "";
    const label = codePart + (option.partName || "");
    return hasPlannedParts && option.stockRoomName
      ? `${label} - ${option.stockRoomName}`
      : label;
  };

  useEffect(() => {
    getData();
    getPlannedParts();
    if (editData?.id) {
      getReservationItems();
    }
  }, []);

  const getPlannedParts = async () => {
    const link = masterAPI["workOrder"].completeDetail;
    await axiosPrivate
      .get(`${link}/${workOrderId}`)
      .then((res) => {
        setPlannedParts(res.data.result?.workOrderTaskPartsList || []);
      })
      .catch((e) => {
        setPlannedParts([]);
        console.log(e, "getcompletedetails error");
      });
  };

  const getReservationItems = async () => {
    const link = masterAPI["partsReservation"].getPartReservationItems;
    await axiosPrivate
      .get(`${link}/${editData.id}`)
      .then((res) => {
        const result = res.data.result ?? res.data;
        const reservations = Array.isArray(result) ? result : [result];
        const items = reservations.flatMap(
          (reservation) => reservation.partsReservationItemsList || []
        );
        setAddedParts(
          items.map((item) => ({
            id: item.id,
            partId: item.partsId,
            quantity: item.quantity,
            stockRoomId: item.stockRoomId,
            stockRoomName: item.stockRoomName,
            partName: item.partName,
            partCode: item.partName
              ? `${item.partCode} - ${item.partName}`
              : item.partCode,
          }))
        );
      })
      .catch((e) => {
        console.log(e, "getPartReservationItems");
      });
  };

  const getData = async () => {
    setLoadingParts(true);
    const partLink = masterAPI["inventory"].getAll;
    await axiosPrivate
      .get(`${partLink}`)
      .then((res) => {
        const resData=res.data.result
        const uniqueParts = Array.from(
          new Map(resData.map(item => [item.partId, item])).values()
         );
        setPartData(uniqueParts || []);
      })
      .catch((e) => {
        setPartData([]);
        console.log(e, "Part WorkOrder");
      });
    setLoadingParts(false);
  };

  const handlePartChange = async (value) => {
    if (hasPlannedParts) {
      setPartItem({
        ...partItem,
        partId: value.partId,
        stockRoomId: value.stockRoomId,
        stockRoomName: value.stockRoomName,
        stockRoom: [],
      });
      const reservationQtyLink = masterAPI["inventory"].getPartsReservationQuantity;
      await axiosPrivate
        .get(`${reservationQtyLink}/${value.partId}/${value.stockRoomId}`)
        .then((res) => {
          const result = res.data?.result[0];
          setMaxQuantity(result?.balanceQuantity ?? "");
        })
        .catch((e) => {
          console.log(e, "reservation quantity error");
          setMaxQuantity("");
        });
      return;
    }

    const srLink = masterAPI["selectList"].stockRoomByPart;
    await axiosPrivate
      .get(`${srLink}/${value.partId}/true`)
      .then((res) => {
        setPartItem({
          ...partItem,
          partId: value.partId,
          stockRoomId: null,
          stockRoom: Array.isArray(res.data) ? res.data : [],
        });
      })
      .catch(() => {
        setPartItem({
          ...partItem,
          partId: value.partId,
          stockRoom: [],
        });
      });
  };

  const handleStockRoomChange = async (value) => {
    setPartItem({
      ...partItem,
      stockRoomId: value.value,
    });
    // Fetch available reservation quantity for this part + stock room
    const reservationQtyLink = masterAPI["inventory"].getPartsReservationQuantity;
    await axiosPrivate
      .get(`${reservationQtyLink}/${partItem.partId}/${value.value}`)
      .then((res) => {
        const result = res.data?.result[0];
        const qty = result?.balanceQuantity ;
        setMaxQuantity(qty ?? "");
      })
      .catch((e) => {
        console.log(e, "reservation quantity error");
        setMaxQuantity("");
      });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "quantity") {
      if (
        value !== "" &&
        maxQuantity !== "" &&
        Number(value) > Number(maxQuantity)
      ) {
        toast.warn(`Max Quantity ${maxQuantity}`);
        return;
      }
    }
    setPartItem({ ...partItem, [name]: value });
  };

  const buildPartItem = (item) => {
    const partObj = partData.find((p) => p.partId === item.partId);
    const stockRoomObj = item.stockRoom?.find(
      (s) => s.value === item.stockRoomId
    );
    return {
      ...item,
      quantity: Number(item.quantity),
      partCode: partObj
        ? (partObj.code ? partObj.code + " - " : "") + (partObj.partName || "")
        : item.partCode || "",
      partName: partObj?.partName || item.partName || "",
      stockRoomName: stockRoomObj ? stockRoomObj.text : item.stockRoomName || "",
    };
  };

  const resetPartItem = () => {
    setPartItem({
      partId: null,
      stockRoomId: null,
      quantity: 0,
      stockRoom: [],
    });
    setPartSelect("");
    setSrSelect("");
    setMaxQuantity("");
  };

  const handleAddPartList = () => {
    setAddedParts([...addedParts, buildPartItem(partItem)]);
    resetPartItem();
  };

  const handleEditPart = async (item, index) => {
    // Hide the row being edited from the list (it moves into the input fields)
    setAddedParts(addedParts.filter((fil, i) => i !== index));
    setPartSelect(item.partCode || "");
    setSrSelect(item.stockRoomName || "");

    // Fetch stock rooms for this part (not needed when the stock room is fixed by the work plan)
    let stockRoomOptions = [];
    if (!hasPlannedParts) {
      const srLink = masterAPI["selectList"].stockRoomByPart;
      await axiosPrivate
        .get(`${srLink}/${item.partId}/false`)
        .then((res) => {
          stockRoomOptions = Array.isArray(res.data) ? res.data : [];
        })
        .catch(() => {
          stockRoomOptions = [];
        });
    }

    setPartItem({
      id: item.id,
      partId: item.partId,
      stockRoomId: item.stockRoomId,
      quantity: item.quantity,
      partCode: item.partCode,
      partName: item.partName,
      stockRoomName: item.stockRoomName,
      stockRoom: stockRoomOptions,
    });

    // Fetch available reservation quantity for this part + stock room
    const reservationQtyLink = masterAPI["inventory"].getPartsReservationQuantity;
    await axiosPrivate
      .get(`${reservationQtyLink}/${item.partId}/${item.stockRoomId}`)
      .then((res) => {
        const result = res.data?.result;
        const qty =
          typeof result === "number"
            ? result
            : result?.balanceQuantity ?? result?.quantity ?? "";
        setMaxQuantity(qty ?? "");
      })
      .catch(() => {
        setMaxQuantity("");
      });
  };

  const deleteAddedPart = (index) => {
    setAddedParts(addedParts.filter((fil, i) => i !== index));
  };

  const handlePartSubmit = async () => {
    // If an item is in progress in the input fields, include it in the list
    let finalParts = addedParts;
    if (
      partItem.partId &&
      partItem.stockRoomId &&
      Number(partItem.quantity) > 0
    ) {
      finalParts = [...addedParts, buildPartItem(partItem)];
    }

    if (finalParts.length === 0) {
      toast.warning("Please add at least one part");
      return;
    }

    setLoading(true);
    const wotlink = masterAPI["partsReservation"].create;
    const emptyId = "00000000-0000-0000-0000-000000000000";

    const partsReservationItemsList = finalParts.map((item) => ({
      id: item.id || emptyId,
      partsId: item.partId,
      quantity: item.quantity,
      stockRoomId: item.stockRoomId,
      stockRoomName: item.stockRoomName,
      partName: item.partName,
      partCode: item.partCode,
    }));

    const editStatuses = [partsReservationStatus.PPRAPPR, partsReservationStatus.REJT, partsReservationStatus.OPEN];
    const payload = {
      id: editStatuses.includes(editData?.status?.toUpperCase())
        ? editData.id
        : emptyId,
      workOrderId: workOrderId,
      partsReservationItemsList: partsReservationItemsList,
    };

    await axiosPrivate
      .post(`${wotlink}`, payload)
      .then((res) => {
        // toast.success("Parts added successfully");
        setLoading(false);
        handleCloseClick();
      })
      .catch((e) => {
        console.log(e, "createwotaskpart");
        // toast.error("Error saving parts");
        setLoading(false);
      });
  };

  const defaultFilterOptions = createFilterOptions();
  const filterOptions = (options, state) => {
    return defaultFilterOptions(options, state);
  };
  const CustomPaper = (props) => {
    return <Paper elevation={8} {...props} />;
  };

  const columns = [
    { Header: "Part", accessor: "partCode" },
    { Header: "Stock Room", accessor: "stockRoomName" },
    { Header: "Quantity", accessor: "quantity" },
    {
      Header: "Action",
      accessor: "partId",
      Cell: ({ cell }) => (
        <Box sx={{ whiteSpace: "nowrap" }}>
          <IconButton
            onClick={() => handleEditPart(cell.row.original, cell.row.index)}
          >
            <EditOutlined
              sx={{
                fontSize: color.masterEditDelButton,
                color: color.masterEditDelButtonColor,
              }}
            />
          </IconButton>
          <IconButton onClick={() => deleteAddedPart(cell.row.index)}>
            <DeleteOutlined
              sx={{
                fontSize: color.masterEditDelButton,
                color: color.masterEditDelButtonColor,
              }}
            />
          </IconButton>
        </Box>
      ),
      disableFilters: true,
    },
  ];

  return (
    <Box sx={{ p: color.masterPageDialogPadding }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Typography
          sx={{
            color: color.masterDialogTitleColor,
            fontSize: color.masterDialogFontSize,
            // fontWeight: 500,
          }}
        >
          {editData ? "Edit Part" : "Add Part"}
        </Typography>
        <Box>
          <IconButton onClick={handlePartSubmit} disabled={loading}>
            {loading ? (
              <CircularProgress size={20} sx={{ color: color.TableButtonColor }} />
            ) : (
              <Save sx={{ color: color.TableButtonColor }} />
            )}
          </IconButton>
          <IconButton onClick={handleCloseClick}>
            <Clear />
          </IconButton>
        </Box>
      </Box>
      <Divider sx={{ border: "1px solid rgb(0 0 0 / 17%)", mb: "15px" }} />

      <Grid container spacing={"1rem"}>
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Typography
              sx={{
                color: color.masterDialogTitleColor,
                fontSize: color.masterDialogFontSize,
              }}
            >
              Add Item
            </Typography>
            <IconButton
              sx={{ backgroundColor: color.TableButtonColor }}
              onClick={handleAddPartList}
              disabled={
                !partItem.partId || !partItem.stockRoomId || partItem.quantity <= 0
              }
            >
              <Add sx={{ color: "white" }} />
            </IconButton>
          </Box>
          <Divider sx={{ border: "1px solid rgb(0 0 0 / 17%)", mb: "15px" }} />
        </Grid>
        <Grid container item xs={12} spacing={"1rem"}>
          <Grid item xs={12} md={4}>
            <Typography
              sx={{
                fontWeight: color.labelFontWeight,
                mb: color.labelmb,
                fontSize: color.labelFont,
                color: color.labelColor,
              }}
            >
              Part
            </Typography>
            <Autocomplete
              value={
                hasPlannedParts
                  ? partOptions.find(
                      (p) =>
                        p.partId === partItem.partId &&
                        p.stockRoomId === partItem.stockRoomId
                    ) || null
                  : partOptions.find((p) => p.partId === partItem.partId) ||
                    null
              }
              inputValue={partSelect}
              fullWidth
              filterOptions={filterOptions}
              loading={!hasPlannedParts && loadingParts}
              options={partOptions}
              PaperComponent={CustomPaper}
              getOptionLabel={getPartOptionLabel}
              isOptionEqualToValue={(option, value) =>
                hasPlannedParts
                  ? option.partId === value?.partId &&
                    option.stockRoomId === value?.stockRoomId
                  : option.partId === value?.partId
              }
              onChange={(event, value) => {
                if (value !== null) {
                  handlePartChange(value);
                  setPartSelect(getPartOptionLabel(value));
                } else {
                  setPartSelect("");
                  setPartItem({
                    partId: null,
                    stockRoomId: null,
                    quantity: 0,
                    stockRoom: [],
                  });
                  setSrSelect("");
                  setMaxQuantity("");
                }
              }}
              onInputChange={(event, newInputValue, reason) => {
                if (reason !== 'reset') setPartSelect(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Part"
                  variant="outlined"
                  size="small"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loadingParts ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography
              sx={{
                fontWeight: color.labelFontWeight,
                mb: color.labelmb,
                fontSize: color.labelFont,
                color: color.labelColor,
              }}
            >
              Stock Room
            </Typography>
            {hasPlannedParts ? (
              <TextField
                value={partItem.stockRoomName || ""}
                size="small"
                fullWidth
                InputProps={{ readOnly: true }}
                sx={{
                  ".MuiInputBase-root": {
                    backgroundColor: "#F5F5F5",
                  },
                  ".MuiInputBase-input": {
                    color: "#555555",
                  },
                }}
              />
            ) : (
              <Autocomplete
                value={
                  partItem.stockRoom.find((s) => s.value === partItem.stockRoomId) ||
                  null
                }
                inputValue={srSelect}
                fullWidth
                filterOptions={filterOptions}
                options={partItem.stockRoom}
                PaperComponent={CustomPaper}
                getOptionLabel={(option) => option.text || option}
                isOptionEqualToValue={(option, value) =>
                  option.value === value?.value
                }
                disabled={!partItem.partId}
                onChange={(event, value) => {
                  if (value !== null) {
                    handleStockRoomChange(value);
                    setSrSelect(value.text);
                  } else {
                    setSrSelect("");
                    setPartItem({
                      ...partItem,
                      stockRoomId: null,
                    });
                    setMaxQuantity("");
                  }
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason !== 'reset') setSrSelect(newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      partItem.partId ? "Select Stock Room" : "Select Part First"
                    }
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                )}
              />
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography
              sx={{
                fontWeight: color.labelFontWeight,
                mb: color.labelmb,
                fontSize: color.labelFont,
                color: color.labelColor,
              }}
            >
              Quantity
            </Typography>
            <TextField
              size={"small"}
              type="number"
              name="quantity"
              value={partItem.quantity}
              placeholder="Enter Quantity"
              fullWidth
              onChange={handleChange}
               InputProps={{
                  endAdornment: (
                      <InputAdornment position="end">
                        {maxQuantity !== "" ? `Max: ${maxQuantity}` : null}
                       </InputAdornment>
                                    ),
                                  }}
             
              sx={{
                ".MuiInputBase-input": {
                  "&::placeholder": {
                    opacity: 1,
                    color: color.placeholderColor,
                    fontSize: "13px",
                  },
                },
              }}
            />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Typography
              sx={{
                color: color.masterDialogTitleColor,
                fontSize: color.masterDialogFontSize,
              }}
            >
              Part List
            </Typography>
          </Box>
          <Divider sx={{ border: "1px solid rgb(0 0 0 / 17%)", mb: "15px" }} />
        </Grid>
        <Grid item xs={12}>
          <NormalTable data={addedParts} columns={columns} filt={true} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default CreatePart;

